import store from '@/store';
import { BENEFIT_DEFAULT_IMAGES } from '@/constants/benefitDefaultImages';
import { resolveRecommendedDefaultImageId } from '@/helpers/themeImageResolver';

/**
 * Resolves the display image URL for a benefit, with theme-aware override.
 *
 * Priority order:
 * 1. media_type = 'image' → use avatar.image_url (custom uploaded image)
 * 2. media_type = 'default' → resolve dynamically from active theme
 *    (ignores the static default_image_url stored in DB; always reflects current theme)
 * 3. Legacy benefit (no media_type) → use avatar.image_url, fallback chain
 *
 * @param {object} benefit - Benefit object from API
 * @returns {string} Resolved image URL
 */
export function resolveBenefitImageUrl(benefit) {
    if (!benefit) return '';

    if (benefit.media_type === 'image') {
        return benefit.avatar?.image_url || benefit.resolved_avatar_image_url || '';
    }

    if (benefit.media_type === 'default') {
        const themeCorporate = store.state.ThemeCorporate?.themeCorporate;
        const recommendedId = resolveRecommendedDefaultImageId(themeCorporate);
        const imageObj = BENEFIT_DEFAULT_IMAGES.find((img) => img.id === recommendedId);

        // Fall back to stored default_image_url only if theme cannot be resolved
        return imageObj?.image_url || benefit.default_image_url || '';
    }

    // Legacy: no media_type — use avatar relation with full fallback chain
    return benefit.avatar?.image_url
        || benefit.resolved_avatar_image_url
        || benefit.default_image_url
        || '';
}

/**
 * Vue-options-compatible mixin providing theme-aware image computed properties
 * for components that receive a single benefit or voucher prop.
 */
export const benefitImageMixin = {
    computed: {
        benefitImageUrlFromTheme() {
            return resolveBenefitImageUrl(this.benefit);
        },
        voucherImageUrlFromTheme() {
            return this.voucher?.avatar?.image_url
                || this.voucher?.resolved_avatar_image_url
                || this.voucher?.default_image_url
                || this.voucher?.image_url
                || '';
        }
    }
};
